import { z } from 'zod';
import { CoordinatesSchema } from './property.js';
import {
  CompleteRegionPriceListSchema,
  RegionPriceListSchema,
  RegionPriceSchema,
} from './billing.js';

export const RegionStatusSchema = z.enum(['active', 'inactive']);
export type RegionStatus = z.infer<typeof RegionStatusSchema>;

export const RegionBoundsSchema = z.object({
  north: z.number(),
  south: z.number(),
  east: z.number(),
  west: z.number(),
});
export type RegionBounds = z.infer<typeof RegionBoundsSchema>;

export const SupportedRegionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  center: CoordinatesSchema,
  radiusMiles: z.number().optional(),
  polygon: z.array(CoordinatesSchema),
  bounds: RegionBoundsSchema,
  status: RegionStatusSchema,
  launchDate: z.string().optional(),
});
export type SupportedRegion = z.infer<typeof SupportedRegionSchema>;

export const CreateRegionRequestSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  polygon: z.array(CoordinatesSchema).min(3, 'At least 3 boundary points required'),
  status: RegionStatusSchema.default('active'),
  launchDate: z.string().nullable().optional(),
  /** One row per gateway this region is priced for — every gateway is required at
   *  creation time (CompleteRegionPriceListSchema), not just whichever is being tested
   *  right now. See billing.ts for why update uses a looser, subset-allowed schema. */
  prices: CompleteRegionPriceListSchema,
});
export type CreateRegionRequest = z.infer<typeof CreateRegionRequestSchema>;

// `.partial()` only wraps top-level fields in ZodOptional; `prices` is re-declared below
// with RegionPriceListSchema (not the create-time CompleteRegionPriceListSchema) since a
// legitimate update reprices a subset of gateways — RegionService.syncPrices leaves any
// gateway omitted from the list untouched. Omitting the field entirely (not just an empty
// array) is what leaves prices untouched.
export const UpdateRegionRequestSchema = CreateRegionRequestSchema.partial().extend({
  prices: RegionPriceListSchema.optional(),
});
export type UpdateRegionRequest = z.infer<typeof UpdateRegionRequestSchema>;

/** `GET /admin/regions` read-shape — existing prices alongside the region, for prefilling
 *  the editor. Not part of the public `GET /regions` response. */
export const AdminSupportedRegionSchema = SupportedRegionSchema.extend({
  prices: z.array(RegionPriceSchema),
});
export type AdminSupportedRegion = z.infer<typeof AdminSupportedRegionSchema>;
